<template>
  <div>
    <div v-if="$router.currentRoute.name === 'advanced-revenue-exclusion-periods-period'">
      <div class="pr-2">
        <v-breadcrumbs :items="breadcrumbs">
          <template v-slot:divider>
            <v-icon>chevron_right</v-icon>
          </template>
        </v-breadcrumbs>
      </div>
      <h1 class="mb-4 text-xs-center">{{ isNewRecord ? 'Create Revenue Exclusion Period' : 'Edit Revenue Exclusion Period' }}</h1>

      <v-layout row justify-center>
        <v-flex xs12 sm10 md8 lg7>
          <v-card class="mb-4">
            <v-card-text>
              <v-form ref="form">
                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                    Agency ID
                  </v-flex>
                  <v-flex sm8>
                    <v-select
                      v-model="form.agencyId"
                      :items="agencyOptions"
                      item-text="text"
                      item-value="value"
                      placeholder="Select Agency"
                      :disabled="!isAdmin || apiInProgress"
                    />
                  </v-flex>
                </v-layout>

                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                    Transportation Profile / Vehicle (optional)
                  </v-flex>
                  <v-flex sm8>
                    <transportation-profile-autocomplete
                      v-model="selectedTransportationProfile"
                      placeholder="Filter by licence plate, driver name"
                      :disabled="!isAdmin || apiInProgress"
                      :show-driver="true"
                      :show-owner-account="true"
                    />
                  </v-flex>
                </v-layout>

                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                    Recurrence Type
                  </v-flex>
                  <v-flex sm8>
                    <v-select
                      v-model="form.recurrenceType"
                      :items="recurrenceTypeOptions"
                      placeholder="Select Recurrence Type"
                      :disabled="!isAdmin || apiInProgress"
                    />
                  </v-flex>
                </v-layout>

                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right align-center font-weight-bold" sm4 mt-4 pr-3>
                    Active
                  </v-flex>
                  <v-flex sm8>
                    <v-checkbox
                      label="Toggle exclusion period"
                      v-model="form.isActive"
                      :disabled="!isAdmin || apiInProgress"
                    />
                  </v-flex>
                </v-layout>

                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                    Reason
                  </v-flex>
                  <v-flex sm8>
                    <v-text-field
                      v-model="form.reason"
                      placeholder="Enter reason"
                      :disabled="!isAdmin || apiInProgress"
                    />
                  </v-flex>
                </v-layout>

                <template v-if="form.recurrenceType === 'once'">
                  <v-layout row wrap mb-1>
                    <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                      Period Start
                    </v-flex>
                    <v-flex sm8>
                      <v-text-field
                        v-model="form.periodStartDate"
                        type="date"
                        label="Period Start"
                        :disabled="!isAdmin || apiInProgress"
                      />
                    </v-flex>
                  </v-layout>

                  <v-layout row wrap mb-1>
                    <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                      Period End
                    </v-flex>
                    <v-flex sm8>
                      <v-text-field
                        v-model="form.periodEndDate"
                        type="date"
                        label="Period End"
                        :disabled="!isAdmin || apiInProgress"
                      />
                    </v-flex>
                  </v-layout>
                </template>

                <template v-if="form.recurrenceType !== 'once'">
                  <v-layout row wrap mb-1>
                    <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                      Effective Start (optional)
                    </v-flex>
                    <v-flex sm8>
                      <v-text-field
                        v-model="form.periodStartDate"
                        type="date"
                        label="Effective Start (optional)"
                        :disabled="!isAdmin || apiInProgress"
                      />
                    </v-flex>
                  </v-layout>

                  <v-layout row wrap mb-1>
                    <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                      Effective End (optional)
                    </v-flex>
                    <v-flex sm8>
                      <v-text-field
                        v-model="form.periodEndDate"
                        type="date"
                        label="Effective End (optional)"
                        :disabled="!isAdmin || apiInProgress"
                      />
                    </v-flex>
                  </v-layout>
                </template>

                <template v-if="form.recurrenceType === 'weekly'">
                  <v-layout row wrap mb-1>
                    <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                      Day Of Week Start
                    </v-flex>
                    <v-flex sm8>
                      <v-select
                        v-model="form.dayOfWeekStart"
                        :items="dayOfWeekOptions"
                        item-text="text"
                        item-value="value"
                        label="Day Of Week Start"
                        :disabled="!isAdmin || apiInProgress"
                      />
                    </v-flex>
                  </v-layout>

                  <v-layout row wrap mb-1>
                    <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                      Day Of Week End (optional)
                    </v-flex>
                    <v-flex sm8>
                      <v-select
                        v-model="form.dayOfWeekEnd"
                        :items="dayOfWeekOptions"
                        item-text="text"
                        item-value="value"
                        label="Day Of Week End (optional)"
                        clearable
                        :disabled="!isAdmin || apiInProgress"
                      />
                    </v-flex>
                  </v-layout>
                </template>

                <template v-if="form.recurrenceType === 'monthly'">
                  <v-layout row wrap mb-1>
                    <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                      Day Of Month Start
                    </v-flex>
                    <v-flex sm8>
                      <v-text-field
                        v-model.number="form.dayOfMonthStart"
                        type="number"
                        min="1"
                        max="31"
                        label="Day Of Month Start"
                        :disabled="!isAdmin || apiInProgress"
                      />
                    </v-flex>
                  </v-layout>

                  <v-layout row wrap mb-1>
                    <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                      Day Of Month End (optional)
                    </v-flex>
                    <v-flex sm8>
                      <v-text-field
                        v-model.number="form.dayOfMonthEnd"
                        type="number"
                        min="1"
                        max="31"
                        label="Day Of Month End (optional)"
                        :disabled="!isAdmin || apiInProgress"
                      />
                    </v-flex>
                  </v-layout>
                </template>

                <template v-if="form.recurrenceType === 'yearly'">
                  <v-layout row wrap mb-1>
                    <v-flex class="text-xs-right font-weight-bold" mt-4 sm4 pr-3>
                      Use Holiday Key
                    </v-flex>
                    <v-flex sm8>
                      <v-checkbox
                        v-model="useHolidayKey"
                        label="Select holiday key"
                        :disabled="!isAdmin || apiInProgress"
                      />
                    </v-flex>
                  </v-layout>

                  <template v-if="useHolidayKey">
                    <v-layout row wrap mb-1>
                      <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                        Holiday Key
                      </v-flex>
                      <v-flex sm8>
                        <v-select
                          v-model="form.holidayKey"
                          :items="holidayKeyOptions"
                          label="Holiday Key"
                          :disabled="!isAdmin || apiInProgress"
                        />
                      </v-flex>
                    </v-layout>

                    <v-layout row wrap mb-1>
                      <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                        Holiday Offset Days
                      </v-flex>
                      <v-flex sm8>
                        <v-text-field
                          v-model.number="form.holidayOffsetDays"
                          type="number"
                          label="Holiday Offset Days"
                          :disabled="!isAdmin || apiInProgress"
                        />
                      </v-flex>
                    </v-layout>

                    <v-layout row wrap mb-1>
                      <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                        Holiday Duration Days
                      </v-flex>
                      <v-flex sm8>
                        <v-text-field
                          v-model.number="form.holidayDurationDays"
                          type="number"
                          min="1"
                          label="Holiday Duration Days"
                          :disabled="!isAdmin || apiInProgress"
                        />
                      </v-flex>
                    </v-layout>

                    <v-layout row wrap mb-1 v-if="isManualHolidayKey">
                      <v-flex sm4 />
                      <v-flex sm8>
                        <v-alert :value="true" type="info" outline>
                          This holiday cannot be computed automatically. Create individual "once" records for each year's dates.
                        </v-alert>
                      </v-flex>
                    </v-layout>
                  </template>

                  <template v-else>
                    <v-layout row wrap mb-1>
                      <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                        Month Start
                      </v-flex>
                      <v-flex sm8>
                        <v-select
                          v-model.number="form.monthStart"
                          :items="monthOptions"
                          item-text="text"
                          item-value="value"
                          placeholder="Select month start"
                          :disabled="!isAdmin || apiInProgress"
                        />
                      </v-flex>
                    </v-layout>

                    <v-layout row wrap mb-1>
                      <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                        Day Of Month Start
                      </v-flex>
                      <v-flex sm8>
                        <v-text-field
                          v-model.number="form.dayOfMonthStart"
                          type="number"
                          min="1"
                          max="31"
                          placeholder="Enter day of month start"
                          :disabled="!isAdmin || apiInProgress"
                        />
                      </v-flex>
                    </v-layout>

                    <v-layout row wrap mb-1>
                      <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                        Month End (optional)
                      </v-flex>
                      <v-flex sm8>
                        <v-select
                          v-model.number="form.monthEnd"
                          :items="monthOptions"
                          item-text="text"
                          item-value="value"
                          placeholder="Select month end (optional)"
                          clearable
                          :disabled="!isAdmin || apiInProgress"
                        />
                      </v-flex>
                    </v-layout>

                    <v-layout row wrap mb-1>
                      <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                        Day Of Month End (optional)
                      </v-flex>
                      <v-flex sm8>
                        <v-text-field
                          v-model.number="form.dayOfMonthEnd"
                          type="number"
                          min="1"
                          max="31"
                          placeholder="Enter day of month end (optional)"
                          :disabled="!isAdmin || apiInProgress"
                        />
                      </v-flex>
                    </v-layout>
                  </template>
                </template>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn flat color="warning" :disabled="apiInProgress" @click="navigateToExclusionList()">Cancel</v-btn>
              <v-spacer />
              <v-btn
                color="warning"
                flat
                :disabled="!isAdmin || isNewRecord || apiInProgress"
                @click="deleteRecord"
              >
                Delete
              </v-btn>
              <v-btn
                color="primary"
                :disabled="!isAdmin || apiInProgress"
                @click="saveRecord"
              >
                Save
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>
    </div>
  </div>
</template>

<script>
import src from './src'

export default src
</script>
